import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Container,
  Fade,
  Grid,
  Stack,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  ISubmissionQuestion,
  getSubmissionById,
  getSubmissionQuestion,
  getSubmissionState,
  resetSubmission,
} from '../../../store/SubmissionSlice';
import {
  ISubTopic,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { getAuthState } from '../../../store/AuthSlice';
import { getDiagnosticBySubmissionId } from '../../../store/DiagnosticSlice';
import { ITopic } from '../../../store/TopicSlice';
import { IQuestionDetailAnswer } from '../../../store/QuestionSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import { TShuffledData, factorial, permutator } from '.';
import Breadcrumbs, {
  BreadcrumbOption,
  getBreadCrumbOptionsByTopicAndSubTopicData,
} from '../../../components/BreadCrumb/Breadcrumbs';
import QuestionListContainer from './components/QuestionListContainer';
import QuestionDetailViewer from './components/QuestionDetailViewer';
import AnswerListItem from './components/AnswerListItem';
import MarkingSchemeViewer from './components/MarkingSchemeViewer';
import OpenEndedQuestionAnswerHandler from './components/OpenEndedQuestionAnswerHandler';
import SubmissionTimer from './components/Timer/SubmissionTimer';
import GradedSection from './components/GradedSection';
import BasicLoader from '../../../components/Loaders/BasicLoader';

const constructBreadcrumbOptions = (
  topicData?: ITopic,
  subTopicData?: ISubTopic
) => {
  const options: Array<BreadcrumbOption> = getBreadCrumbOptionsByTopicAndSubTopicData(
    topicData,
    subTopicData
  );

  options.push({
    type: 'text',
    title: 'Quiz',
    highlight: true,
  });

  return options;
};

const containerStyles: SxProps = {
  background: 'linear-gradient(180deg, #01736D 0%, #001615 100%)',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const ViewQuizSubmission: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const submissionState = useAppSelector(getSubmissionState);
  const authState = useAppSelector(getAuthState);
  const subTopicState = useAppSelector(getSubTopicState);

  const authUser = authState.user;
  const submissionData = submissionState.submission;
  const questionData = submissionState.question;
  const quizSubTopics: string[] = [];
  const subTopicData = subTopicState.subTopic;
  const quizData =
    submissionData && submissionData.quiz ? submissionData.quiz : undefined;

  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const breadcrumbOptions = constructBreadcrumbOptions(topicData, subTopicData);
  const submissionQuestions =
    submissionData && submissionData.questions ? submissionData.questions : [];

  const isDiagnostic =
    (topicData && topicData.name === 'Diagnostic Challenge') || false;

  if (quizData && quizData.subTopics && quizData.subTopics.length) {
    quizData.subTopics.forEach((item) => {
      if (item.subTopic && item.subTopic.name) {
        quizSubTopics.push(item.subTopic.name);
      }
    });
  }

  // State
  const [selectedQuestion, setSelectedQuestion] = useState<
    ISubmissionQuestion | undefined
  >(undefined);

  useEffect(() => {
    if (params.submissionId) {
      dispatch(getSubmissionById(params.submissionId));
    }

    if (params.subtopicId) {
      dispatch(getSubTopicById(params.subtopicId));
    }

    return () => {
      dispatch(resetSubmission());
    };
  }, [params.submissionId]);

  useEffect(() => {
    if (params.submissionId && submissionData && submissionQuestions.length) {
      if (!selectedQuestion) {
        const firstQuestion = submissionQuestions[0];

        setSelectedQuestion(firstQuestion);

        if (firstQuestion && firstQuestion.question) {
          dispatch(
            getSubmissionQuestion({
              submissionId: params.submissionId,
              questionId: firstQuestion.question,
            })
          );
        }
      }

      if (selectedQuestion) {
        const index = submissionQuestions.findIndex(
          (questionItem) => questionItem.question === selectedQuestion.question
        );

        if (index > -1) {
          setSelectedQuestion(submissionQuestions[index]);
        }
      }

      if (isDiagnostic && submissionData.status === 'completed') {
        dispatch(getDiagnosticBySubmissionId(params.submissionId));
      }
    }
  }, [params.submissionId, submissionData]);

  const getShuffledData = () => {
    let shuffledAnswers: Array<IQuestionDetailAnswer> = [];
    let openAiCorrectAnswers: Array<IQuestionDetailAnswer> = [];
    let selectedOpenAiCorrectAnswer: IQuestionDetailAnswer | undefined;

    if (questionData) {
      shuffledAnswers = questionData.answers || [];

      if (submissionData && submissionData.shuffleFactor) {
        shuffledAnswers = permutator(questionData.answers)[
          submissionData.shuffleFactor % factorial(questionData.answers.length)
        ];

        shuffledAnswers = shuffledAnswers.map((item, index) => ({
          ...item,
          order: index,
        }));
      }

      if (shuffledAnswers.length) {
        openAiCorrectAnswers = shuffledAnswers.filter(
          (shuffledQuestion) =>
            shuffledQuestion.isCorrect && shuffledQuestion.openaiResponse
        );

        selectedOpenAiCorrectAnswer = shuffledAnswers.find(
          (shuffledQuestion) =>
            selectedQuestion &&
            selectedQuestion.answer &&
            shuffledQuestion._id === selectedQuestion.answer._id
        );
      }
    }

    return {
      openAiCorrectAnswers,
      selectedOpenAiCorrectAnswer,
      answers: shuffledAnswers,
    };
  };

  const onQuestionBackNavigation = () => {
    if (selectedQuestion) {
      const prevQuestion = submissionQuestions[selectedQuestion.order - 1];

      if (params.submissionId && prevQuestion) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: prevQuestion.question,
          })
        );
      }

      setSelectedQuestion(prevQuestion);
    }
  };

  const onQuestionNextNavigation = () => {
    if (selectedQuestion) {
      const nextQuestion = submissionQuestions[selectedQuestion.order + 1];

      if (params.submissionId && nextQuestion) {
        dispatch(
          getSubmissionQuestion({
            submissionId: params.submissionId,
            questionId: nextQuestion.question,
          })
        );
      }

      setSelectedQuestion(nextQuestion);
    }
  };

  const handleQuestionChange = (question: ISubmissionQuestion) => {
    if (params.submissionId) {
      dispatch(
        getSubmissionQuestion({
          submissionId: params.submissionId,
          questionId: question.question,
        })
      );

      setSelectedQuestion(question);

      window.scrollTo({ top: 0 });
    }
  };

  const shuffledData: TShuffledData = getShuffledData();

  const isQuestionBackNavigationEnabled =
    selectedQuestion && selectedQuestion.order > 0;

  const isQuestionNextNavigationEnabled =
    selectedQuestion && selectedQuestion.order < submissionQuestions.length - 1;

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ padding: '30px 0' }}
        >
          <Breadcrumbs options={breadcrumbOptions} showDropDown={belowMd} />
          {submissionData &&
          authUser &&
          submissionData.timerStart &&
          submissionData.isTimerEnabled &&
          !(isDiagnostic && submissionData.status === 'completed') ? (
            <SubmissionTimer
              submissionData={submissionData}
              isTimeUpAlertVisible={false}
            />
          ) : null}
        </Stack>
        {/* MAIN CONTENT CONTAINER */}
        <div style={{ marginBottom: '30px' }}>
          {submissionData &&
          questionData &&
          selectedQuestion &&
          selectedQuestion.question === questionData._id ? (
            <Fade in timeout={600}>
              <Grid container spacing={5}>
                {/* QUESTION DETAIL VIEWER */}
                <Grid item xs={12} lg={6}>
                  <QuestionDetailViewer
                    selectedQuestion={selectedQuestion}
                    questionData={questionData}
                    submissionData={submissionData}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  {/* ANSWER HANDLER FOR OPEN ENDED QUESTIONS */}
                  <div style={{ marginBottom: '15px' }}>
                    {selectedQuestion.isOpenEnded &&
                    questionData.openEnded &&
                    questionData.openEnded.question ? (
                      <Box>
                        <OpenEndedQuestionAnswerHandler
                          selectedQuestion={selectedQuestion}
                          submissionData={submissionData}
                        />
                      </Box>
                    ) : (
                      // ANSWER HANDLER FOR REGULAR QUESTIONS
                      <Box>
                        <Grid container spacing={2}>
                          {(shuffledData.answers || []).map((item, index) => {
                            return (
                              <Grid item xs={12} key={`an-li-${index}`}>
                                <AnswerListItem
                                  isDisabled
                                  answer={item}
                                  answers={shuffledData.answers}
                                  submission={submissionData}
                                  selectedQuestion={selectedQuestion}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                  </div>
                  {/* QUESTION NAVIGATION CONTAINER */}
                  <div style={{ marginBottom: '15px' }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <div />
                      <Stack direction="row" alignItems="center">
                        <Button
                          variant="contained"
                          color="inherit"
                          startIcon={<ChevronLeftIcon />}
                          onClick={onQuestionBackNavigation}
                          disabled={
                            !isQuestionBackNavigationEnabled ||
                            submissionState.status === 'loading'
                          }
                          style={{
                            backgroundColor: isQuestionBackNavigationEnabled
                              ? 'rgba(248, 250, 252, 0.3)'
                              : 'rgba(248, 250, 252, 0.1)',
                            color: TP_COLORS.white,
                            borderRadius: '30px',
                            fontWeight: 500,
                            marginRight: '10px',
                          }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          color="inherit"
                          endIcon={<ChevronRightIcon />}
                          onClick={onQuestionNextNavigation}
                          disabled={
                            !isQuestionNextNavigationEnabled ||
                            submissionState.status === 'loading'
                          }
                          style={{
                            backgroundColor: isQuestionNextNavigationEnabled
                              ? 'rgba(248, 250, 252, 0.3)'
                              : 'rgba(248, 250, 252, 0.1)',
                            color: TP_COLORS.white,
                            borderRadius: '30px',
                            fontWeight: 500,
                          }}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Stack>
                  </div>
                </Grid>
              </Grid>
            </Fade>
          ) : (
            <BasicLoader loaderProps={{ style: { color: TP_COLORS.white } }} />
          )}
        </div>
        {/* OPEN ENDED GRADE DATA VIEWER */}
        {selectedQuestion &&
        questionData &&
        selectedQuestion.isOpenEnded &&
        questionData.openEnded &&
        questionData.openEnded.question &&
        selectedQuestion.question === questionData._id ? (
          <GradedSection
            selectedQuestion={selectedQuestion}
            questionData={questionData}
          />
        ) : null}
        {/* MARKING SCHEME VIEWER */}
        {submissionData &&
        submissionData.status === 'completed' &&
        questionData &&
        selectedQuestion &&
        selectedQuestion.question === questionData._id ? (
          <Fade in>
            <div style={{ marginBottom: '15px' }}>
              <MarkingSchemeViewer
                questionData={questionData}
                shuffledData={shuffledData}
                selectedQuestion={selectedQuestion}
                submissionData={submissionData}
              />
            </div>
          </Fade>
        ) : null}
      </Container>
      {/* BOTTOM QUESTION LIST CONTAINER */}
      <div style={{ minHeight: '120px' }} />
      {submissionData ? (
        <QuestionListContainer
          isDiagnostic={isDiagnostic}
          submission={submissionData}
          selectedQuestion={selectedQuestion}
          onQuestionClicked={handleQuestionChange}
        />
      ) : null}
    </Box>
  );
};

export default ViewQuizSubmission;
